import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { thumbStyle } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Heart, Search, ArrowLeft, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { PET_COLORS, parseColorField, getColorLabel } from '@/utils/petColors';
import { formatPetDate } from '@/utils/formatDate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 24;

const FoundPets = () => {
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    breed: '',
    color: '',
    location: '',
    dateFrom: '',
    dateTo: '',
  });
  // Filters that have been "applied" (committed via the search button)
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const fetchPets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (appliedFilters.breed) params.append('breed', appliedFilters.breed);
      if (appliedFilters.color) params.append('color', appliedFilters.color);
      if (appliedFilters.location) params.append('location', appliedFilters.location);
      if (appliedFilters.dateFrom) params.append('date_from', appliedFilters.dateFrom);
      if (appliedFilters.dateTo) params.append('date_to', appliedFilters.dateTo);
      params.append('skip', String(page * PAGE_SIZE));
      params.append('limit', String(PAGE_SIZE));

      const response = await axios.get(`${API}/pets/found?${params.toString()}`);
      setPets(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Error fetching pets:', error);
      toast.error('Error al cargar mascotas');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, page]);

  useEffect(() => {
    fetchPets();
  }, [fetchPets]);

  const handleSearch = () => {
    setPage(0);
    setAppliedFilters(filters);
  };

  const maxPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="min-h-screen gradient-mesh">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            data-testid="back-button"
            variant="ghost"
            size="sm"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="rounded-full"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="h-6 w-6 text-primary" fill="currentColor" />
            <h1 className="text-xl font-fraunces font-semibold text-foreground">
              Mascotas Encontradas
            </h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Filters */}
        <Card className="rounded-3xl border-border mb-8">
          <CardHeader>
            <CardTitle className="font-fraunces text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtros
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3 sm:gap-4">
              <Input
                data-testid="breed-filter"
                placeholder="Raza"
                value={filters.breed}
                onChange={(e) => setFilters(prev => ({ ...prev, breed: e.target.value }))}
                className="w-full sm:w-40 rounded-xl"
              />
              <Select
                data-testid="color-filter"
                value={filters.color}
                onValueChange={(v) => setFilters(prev => ({ ...prev, color: v }))}
              >
                <SelectTrigger className="w-full sm:w-44 rounded-xl">
                  <SelectValue placeholder="Color" />
                </SelectTrigger>
                <SelectContent>
                  {PET_COLORS.map(c => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                data-testid="location-filter"
                placeholder="Ubicación"
                value={filters.location}
                onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                className="w-full sm:w-40 rounded-xl"
              />
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label className="text-sm text-muted-foreground whitespace-nowrap">Desde</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  className="flex-1 sm:w-40 rounded-xl"
                />
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <label className="text-sm text-muted-foreground whitespace-nowrap">Hasta</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  className="flex-1 sm:w-40 rounded-xl"
                />
              </div>
              <Button
                data-testid="search-button"
                onClick={handleSearch}
                className="rounded-full bg-primary text-primary-foreground w-full sm:w-auto"
              >
                <Search className="h-4 w-4 mr-2" />
                Buscar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          </div>
        ) : pets.length === 0 ? (
          <Card className="rounded-3xl border-border">
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">
                No se encontraron mascotas encontradas
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pets.map((pet) => (
                <Card
                  key={pet.pet_id}
                  data-testid={`pet-card-${pet.pet_id}`}
                  className="card-hover cursor-pointer rounded-3xl border-border overflow-hidden"
                  onClick={() => navigate(`/pet/${pet.pet_id}`)}
                >
                  {pet.photos && pet.photos.length > 0 && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={pet.photos[0]}
                        alt={pet.name || 'Pet'}
                        className="w-full h-full object-cover"
                        style={thumbStyle(pet.thumbnail_position, pet.thumbnail_zoom)}
                      />
                    </div>
                  )}
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="font-fraunces text-lg">
                        {pet.name || 'Sin nombre'}
                      </CardTitle>
                      <Badge variant="default" className="rounded-full bg-secondary text-secondary-foreground">
                        Encontrada
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-2">
                      {pet.breed || 'Raza desconocida'} • {parseColorField(pet.color).map(getColorLabel).join(', ') || 'Color desconocido'}
                    </p>
                    <p className="text-sm text-muted-foreground mb-2">
                      📍 {pet.location}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Encontrada: {formatPetDate(pet.date_lost_found)}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-2 mt-8">
              <p className="text-sm text-muted-foreground">
                {total} mascota(s) — Página {page + 1} de {maxPage + 1}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={page === 0}
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" /> Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  disabled={page >= maxPage}
                  onClick={() => setPage(p => p + 1)}
                >
                  Siguiente <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FoundPets;
